"use strict";
// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0
Object.defineProperty(exports, "__esModule", { value: true });
exports.initializePushNotifications = void 0;
const core_1 = require("@aws-amplify/core");
const utils_1 = require("@aws-amplify/core/internals/utils");
const pinpoint_1 = require("@aws-amplify/core/internals/providers/pinpoint");
const react_native_1 = require("@aws-amplify/react-native");
const eventListeners_1 = require("../../../../eventListeners");
const utils_2 = require("../../../utils");
const utils_3 = require("../utils");
const { addMessageEventListener, addTokenEventListener, completeNotification, getConstants, registerHeadlessTask, } = (0, react_native_1.loadAmplifyPushNotification)();
const logger = new core_1.ConsoleLogger('Notifications.PushNotification');
const BACKGROUND_TASK_TIMEOUT = 25; // seconds
const initializePushNotifications = () => {
    if ((0, utils_2.isInitialized)()) {
        logger.info('Push notifications have already been enabled');
        return;
    }
    addNativeListeners();
    addAnalyticsListeners();
    (0, utils_2.initialize)();
};
exports.initializePushNotifications = initializePushNotifications;
const addNativeListeners = () => {
    let launchNotificationOpenedListener;
    const { NativeEvent, NativeHeadlessTaskKey } = getConstants();
    const { BACKGROUND_MESSAGE_RECEIVED, FOREGROUND_MESSAGE_RECEIVED, LAUNCH_NOTIFICATION_OPENED, NOTIFICATION_OPENED, TOKEN_RECEIVED, } = NativeEvent;
    // on platforms that can handle headless tasks, register one to broadcast background message received to
    // library listeners
    if (NativeHeadlessTaskKey) {
        registerHeadlessTask(async (message) => {
            // keep headless task running until handlers have completed their work
            await (0, eventListeners_1.notifyEventListenersAndAwaitHandlers)('backgroundMessageReceived', message);
        });
    }
    else if (BACKGROUND_MESSAGE_RECEIVED) {
        // on platforms that can't handle headless tasks, listen for native background message received event and
        // broadcast to library listeners
        addMessageEventListener(BACKGROUND_MESSAGE_RECEIVED, async (message, completionHandlerId) => {
            // keep background task running until handlers have completed their work
            try {
                await Promise.race([
                    (0, eventListeners_1.notifyEventListenersAndAwaitHandlers)('backgroundMessageReceived', message),
                    // background tasks will get suspended and all future tasks be deprioritized by the OS if they run for
                    // more than 30 seconds so we reject with a error in a shorter amount of time to prevent this from
                    // happening
                    new Promise((_resolve, reject) => {
                        setTimeout(() => {
                            reject(new Error(`onNotificationReceivedInBackground handlers should complete their work within ${BACKGROUND_TASK_TIMEOUT} seconds, but they did not.`));
                        }, BACKGROUND_TASK_TIMEOUT * 1000);
                    }),
                ]);
            }
            catch (err) {
                logger.error(err);
            }
            finally {
                // notify native module that handlers have completed their work (or timed out)
                if (completionHandlerId) {
                    completeNotification(completionHandlerId);
                }
            }
        });
    }
    addMessageEventListener(
    // listen for native foreground message received event and broadcast to library listeners
    FOREGROUND_MESSAGE_RECEIVED, message => {
        (0, eventListeners_1.notifyEventListeners)('foregroundMessageReceived', message);
    });
    launchNotificationOpenedListener = LAUNCH_NOTIFICATION_OPENED
        ? addMessageEventListener(
        // listen for native notification opened app (user tapped on notification, opening the app from quit -
        // not background - state) event. This is broadcasted to an internal listener only as it is not intended
        // for use otherwise as it produces inconsistent results when used within React Native app context
        LAUNCH_NOTIFICATION_OPENED, message => {
            (0, eventListeners_1.notifyEventListeners)('launchNotificationOpened', message);
            // once we are done with it we can remove the listener
            launchNotificationOpenedListener?.remove();
            launchNotificationOpenedListener = undefined;
        })
        : undefined;
    addMessageEventListener(
    // listen for native notification opened (user tapped on notification, opening the app from background -
    // not quit - state) event and broadcast to library listeners
    NOTIFICATION_OPENED, message => {
        (0, eventListeners_1.notifyEventListeners)('notificationOpened', message);
        // if we are in this state, we no longer need the listener as the app was launched via some other means
        launchNotificationOpenedListener?.remove();
    });
    addTokenEventListener(
    // listen for native new token event, automatically re-register device with provider using new token and
    // broadcast to library listeners
    TOKEN_RECEIVED, async (token) => {
        // avoid a race condition where two endpoints are created with the same token on a fresh install
        if ((0, utils_2.getToken)() === token) {
            return;
        }
        (0, utils_2.setToken)(token);
        (0, eventListeners_1.notifyEventListeners)('tokenReceived', token);
        try {
            await registerDevice(token);
        }
        catch (err) {
            logger.error('Failed to register device for push notifications', err);
            throw err;
        }
    });
};
const addAnalyticsListeners = () => {
    let launchNotificationOpenedListenerRemover;
    // wire up default Pinpoint message event handling
    (0, eventListeners_1.addEventListener)('backgroundMessageReceived', (0, utils_3.createMessageEventRecorder)('received_background'));
    (0, eventListeners_1.addEventListener)('foregroundMessageReceived', (0, utils_3.createMessageEventRecorder)('received_foreground'));
    launchNotificationOpenedListenerRemover = (0, eventListeners_1.addEventListener)('launchNotificationOpened', (0, utils_3.createMessageEventRecorder)('opened_notification', 
    // once we are done with it we can remove the listener
    () => {
        launchNotificationOpenedListenerRemover?.remove();
        launchNotificationOpenedListenerRemover = undefined;
    }));
    (0, eventListeners_1.addEventListener)('notificationOpened', (0, utils_3.createMessageEventRecorder)('opened_notification', 
    // if we are in this state, we no longer need the listener as the app was launched via some other means
    () => {
        launchNotificationOpenedListenerRemover?.remove();
        launchNotificationOpenedListenerRemover = undefined;
    }));
};
const registerDevice = async (address) => {
    const { credentials, identityId } = await (0, utils_2.resolveCredentials)();
    const { appId, region } = (0, utils_3.resolveConfig)();
    try {
        await (0, pinpoint_1.updateEndpoint)({
            address,
            appId,
            category: 'PushNotification',
            credentials,
            region,
            channelType: (0, utils_3.getChannelType)(),
            identityId,
            userAgentValue: (0, utils_2.getPushNotificationUserAgentString)(utils_1.PushNotificationAction.InitializePushNotifications),
        });
        // always resolve inflight device registration promise here even though the promise is only awaited on by
        // `identifyUser` when no endpoint is found in the cache
        (0, utils_3.resolveInflightDeviceRegistration)();
    }
    catch (underlyingError) {
        (0, utils_3.rejectInflightDeviceRegistration)(underlyingError);
        throw underlyingError;
    }
};
